import { useEffect, useRef, useState, useCallback } from 'react';

export function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  const checkVisibility = useCallback(() => {
    const el = ref.current;
    if (!el || isInView) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 100 && rect.bottom > -100) {
      setIsInView(true);
    }
  }, [isInView]);

  useEffect(() => {
    const el = ref.current;
    if (!el || isInView) return;

    // Check immediately
    checkVisibility();
    if (isInView) return;

    // IntersectionObserver as primary
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin: '100px' }
    );
    observer.observe(el);

    // Scroll listener as fallback
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight + 100 && rect.bottom > -100) {
        setIsInView(true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Also check after a short delay (handles initial render timing)
    const timer = setTimeout(checkVisibility, 100);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
    };
  }, [threshold, isInView, checkVisibility]);

  return { ref, isInView };
}
